import type { EditorialTemplate } from '../../services/api-client';

// =============================================================================
// Template System — defines the structural blueprint for each editorial type
// =============================================================================

export interface TemplateSectionDef {
  /** Section type — determines rendering: richtext, featured_grid, image, callout */
  type: string;
  /** Human-readable label shown in the editor sidebar */
  label: string;
  /** If true, the author cannot remove this section */
  required: boolean;
  /** If true, the author can add additional instances of this section */
  repeatable: boolean;
  /** Pre-filled heading text when section is created */
  defaultHeading?: string;
  /** Placeholder text shown in empty TipTap editor for this section */
  placeholder?: string;
}

export interface TemplateConfig {
  id: EditorialTemplate;
  label: string;
  description: string;
  /** Icon name from @mui/icons-material (for the template picker) */
  iconName: string;
  sections: TemplateSectionDef[];
}

// ---------------------------------------------------------------------------
// Built-in templates
// ---------------------------------------------------------------------------

export const TEMPLATE_CONFIGS: TemplateConfig[] = [
  {
    id: 'article',
    label: 'Article',
    description: 'Standard blog post with an introduction and body sections.',
    iconName: 'Article',
    sections: [
      {
        type: 'richtext',
        label: 'Introduction',
        required: true,
        repeatable: false,
        defaultHeading: '',
        placeholder: 'Write a compelling introduction that hooks the reader...',
      },
      {
        type: 'richtext',
        label: 'Body',
        required: true,
        repeatable: true,
        defaultHeading: '',
        placeholder: 'Continue your article here...',
      },
      {
        type: 'richtext',
        label: 'Call to Action',
        required: false,
        repeatable: false,
        defaultHeading: '',
        placeholder: 'Add a call to action or conclusion...',
      },
    ],
  },
  {
    id: 'guide',
    label: 'Guide',
    description: 'Destination or how-to guide with topic-based sections and tips.',
    iconName: 'Explore',
    sections: [
      {
        type: 'richtext',
        label: 'Introduction',
        required: true,
        repeatable: false,
        defaultHeading: '',
        placeholder: 'Introduce the destination or topic...',
      },
      {
        type: 'richtext',
        label: 'Topic Section',
        required: true,
        repeatable: true,
        defaultHeading: '',
        placeholder: 'Cover a specific aspect of the guide...',
      },
      {
        type: 'richtext',
        label: 'Tips & Advice',
        required: false,
        repeatable: false,
        defaultHeading: 'Tips',
        placeholder: 'Share practical tips for the reader...',
      },
      {
        type: 'richtext',
        label: 'Call to Action',
        required: false,
        repeatable: false,
        defaultHeading: '',
        placeholder: 'Add a call to action or conclusion...',
      },
    ],
  },
  {
    id: 'list',
    label: 'List',
    description: '"Top 10" style listicle with numbered items and a summary.',
    iconName: 'FormatListNumbered',
    sections: [
      {
        type: 'richtext',
        label: 'Introduction',
        required: true,
        repeatable: false,
        defaultHeading: '',
        placeholder: 'Introduce your list and why these items matter...',
      },
      {
        type: 'richtext',
        label: 'List Item',
        required: true,
        repeatable: true,
        defaultHeading: '',
        placeholder: 'Describe this list item...',
      },
      {
        type: 'richtext',
        label: 'Summary',
        required: false,
        repeatable: false,
        defaultHeading: 'Summary',
        placeholder: 'Wrap up and summarize the key takeaways...',
      },
      {
        type: 'richtext',
        label: 'Call to Action',
        required: false,
        repeatable: false,
        defaultHeading: '',
        placeholder: 'Add a call to action...',
      },
    ],
  },
  {
    id: 'collection',
    label: 'Collection',
    description: 'Curated collection page featuring cities or locations.',
    iconName: 'Collections',
    sections: [
      {
        type: 'richtext',
        label: 'Introduction',
        required: true,
        repeatable: false,
        defaultHeading: '',
        placeholder: 'Introduce this curated collection...',
      },
      {
        type: 'featured_grid',
        label: 'Featured Items',
        required: true,
        repeatable: false,
        defaultHeading: 'Featured',
        placeholder: 'Add featured cities or locations to this grid...',
      },
      {
        type: 'richtext',
        label: 'Call to Action',
        required: false,
        repeatable: false,
        defaultHeading: '',
        placeholder: 'Add a call to action...',
      },
    ],
  },
];

/** Look up template config by ID */
export function getTemplateConfig(templateId: EditorialTemplate): TemplateConfig | undefined {
  return TEMPLATE_CONFIGS.find((t) => t.id === templateId);
}
